export default class URLX {
  public hiddenParams = new URLSearchParams();
  private _url: URL;
  constructor(url: string, base?: string);
  constructor(url: string | URLX);
  constructor(url: string | URLX, base = location.href) {
    // Wrapping native `URL` since TypeScript transpiles classes
    // with `super.call` instead of `new super` targeting ES5, which
    // causes `Uncaught TypeError: Failed to construct 'URL'` error.
    // May extend URL instead when classes wont be transpiled to ES5.
    if (typeof url === 'string') {
      this._url = new URL(url, base);
    } else if (url instanceof URLX) {
      // Cloning
      this._url = new URL(url.href);

      for (const entry of url.hiddenParams.entries()) {
        this.hiddenParams.append(entry[0], entry[1]);
      }
    } else {
      throw new Error(`Invalid URL: Expecting string or URLX but \`${url}\` was received`)
    }
  }

  // Native URL
  get hash(): string {
    return this._url.hash;
  }
  set hash(value: string) {
    this._url.hash = value;
  }
  get host(): string {
    return this._url.host;
  }
  set host(value: string) {
    this._url.host = value;
  }
  get hostname(): string {
    return this._url.hostname;
  }
  set hostname(value: string) {
    this._url.hostname = value;
  }
  get href(): string {
    return this._url.href;
  }
  set href(value: string) {
    this._url.href = value;
  }
  get origin(): string {
    return this._url.origin;
  }
  get password(): string {
    return this._url.password;
  }
  set password(value: string) {
    this._url.password = value;
  }
  get pathname(): string {
    return this._url.pathname;
  }
  set pathname(value: string) {
    this._url.pathname = value;
  }
  get port(): string {
    return this._url.port;
  }
  set port(value: string) {
    this._url.port = value;
  }
  get protocol(): string {
    return this._url.protocol;
  }
  set protocol(value: string) {
    this._url.protocol = value;
  }
  get search(): string {
    return this._url.search;
  }
  set search(value: string) {
    this._url.search = value;
  }
  get username(): string {
    return this._url.username;
  }
  set username(value: string) {
    this._url.username = value;
  }
  get searchParams(): URLSearchParams {
    return this._url.searchParams;
  }
  toString(): string {
    return this._url.toString();
  }

  // Custom URL
  get hiddenHref() {
    let hasHiddenParams = false;
    const params = new URLSearchParams(this.search);
    for (const entry of this.hiddenParams.entries()) {
      hasHiddenParams = true;
      params.append(entry[0], entry[1]);
    }

    if (!hasHiddenParams) {
      return this.href;
    }

    const hiddenUrl = new URL(this.href);
    hiddenUrl.search = params.toString();
    return hiddenUrl.href;
  }
}

export function relativeUrl(url: URLX, includeHash = true) {
  if (!isInternalUrl(url) || url.username || url.password) {
    return url.href;
  }

  const query = url.searchParams.toString();
  return url.pathname + (query ? `?${query}` : '') + (includeHash ? url.hash : '');
}

export function currentUrl() {
  return new URLX(location.href);
}

export function normalizeUrl(url: string): string {
  return new URLX(url).href;
}

export function equalsUrl(urlA: URLX, urlB: URLX, compareHash = false, compareHiddenParams = false) {
  if (
    urlA.host === urlB.host &&
    urlA.pathname === urlB.pathname &&
    equalsParams(urlA.searchParams, urlB.searchParams)
  ) {
    if (compareHiddenParams && !equalsParams(urlA.hiddenParams, urlB.hiddenParams)) {
      return false;
    }
    return compareHash ? urlA.hash === urlB.hash : true;
  }
  return false;
}

function equalsParams(paramsA: URLSearchParams, paramsB: URLSearchParams): boolean {
  const keysA = new Set(paramsA.keys());
  const keysB = new Set(paramsB.keys());

  if (keysA.size !== keysB.size) {
    return false;
  }

  for (const key of keysA) {
    const valuesA = paramsA.getAll(key);
    const valuesB = paramsB.getAll(key);

    if (valuesA.length !== valuesB.length) {
      return false;
    }

    for (const value of valuesA) {
      if (!valuesB.includes(value)) {
        return false;
      }
    }
  }

  return true;
}

export function isInternalUrl(url: HTMLAnchorElement | Location | URL | URLX) {
  if (!['http:', 'https:'].includes(url.protocol)) {
    return false;
  }
  if (url.host !== location.host) {
    return false;
  }
  return true;
}

export function pathCombine(first: string, ...rest: string[]) {
  let result = first;
  for (const path of rest) {
    if (result[result.length - 1] !== '/') {
      result += '/';
    }
    const current = path[0] === '/' ? path.substr(1) : path;
    if (current) {
      result += current;
    }
  }
  return result;
}
